import { useEffect, useRef, useState, useCallback } from 'react';
import * as Cesium from 'cesium';
import { useLegendForm, useLegendUI } from '../context/MapLegendContext';
import { useSketchProperitesContext } from '../context/SketchPropertiesContext';
import MapPortalService from '../services/MapPortal/mapportal.service';
import { Sketch, SketchItem } from '../models/sketch.model';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  extractPositionsFromEntity,
  getPropsObject,
  makePolygonEditable,
  makePolylineEditable,
  makePointEditable,
  applyEntityStyles,
} from '../shared/utils/sketchHelpers';

export const useSketchLogic = (viewerRef: any, mapId: string) => {
  const [sketches, setSketches] = useState<Sketch[]>([]);
  const [drawingMode, setDrawingMode] = useState('');

  const { selectedSketch, setSelectedSketch, selectedSketchItem, setSelectedSketchItem, setSketchDrawerState, sketchDrawerState } = useLegendForm();

  const { setSketchDrawerVisible, sketchDrawerVisible } = useLegendUI();

  const sketchProps = useSketchProperitesContext();
  const { setState: setSketchPropsState } = useSketchProperitesContext();

  const { getMapSketches, createMapSketch, editMapSketch, deleteMapSketch, addSketchItems, editSketchItem, deleteSketchItem } = MapPortalService();
  const { data: mapSketchesResponse } = useQuery({
    ...getMapSketches(mapId !== 'new-map' ? Number(mapId) : undefined),
    enabled: mapId !== 'new-map' && !!mapId,
  });
  const { mutateAsync: createMapSketchAsync } = useMutation(createMapSketch());
  const { mutateAsync: editMapSketchAsync } = useMutation(editMapSketch());
  const { mutateAsync: deleteMapSketchAsync } = useMutation(deleteMapSketch());
  const { mutateAsync: addSketchItemsAsync } = useMutation(addSketchItems());
  const { mutateAsync: editSketchItemAsync } = useMutation(editSketchItem());
  const { mutateAsync: deleteSketchItemAsync } = useMutation(deleteSketchItem());

  const [pendingTextClick, setPendingTextClick] = useState<Cesium.Cartesian3 | null>(null);

  const pointsRef = useRef<Cesium.Cartesian3[]>([]);
  const startPointRef = useRef<Cesium.Cartesian3 | null>(null);
  const activeEditRef = useRef<{ destroy: () => void; updateHandles?: (color: string) => void } | null>(null);
  const editingEntityRef = useRef<Cesium.Entity | null>(null);
  const geometryEditedRef = useRef(false);
  const selectedSketchItemRef = useRef(selectedSketchItem);

  useEffect(() => {
    selectedSketchItemRef.current = selectedSketchItem;
  }, [selectedSketchItem]);

  // Fetch initial sketches
  useEffect(() => {
    if (mapSketchesResponse) {
      setSketches(mapSketchesResponse || []);
    }
  }, [mapSketchesResponse]);

  useEffect(() => {
    setDrawingMode('');
    pointsRef.current = [];
    startPointRef.current = null;
    geometryEditedRef.current = false;
  }, [mapId]);

  // Cleanup on drawer close
  useEffect(() => {
    if (!sketchDrawerVisible) {
      setSketchDrawerState('create');
      setSelectedSketch(null);
      editingEntityRef.current = null;
      if (activeEditRef.current) {
        activeEditRef.current.destroy();
        activeEditRef.current = null;
      }
      viewerRef.current?.dataSources.removeAll();
      viewerRef.current?.entities.removeAll();
    }
  }, [sketchDrawerVisible]);

  // Handle visual updates when properties change
  useEffect(() => {
    const entity = editingEntityRef.current;
    if (!entity) return;

    // Re-apply styles live
    applyEntityStyles(entity, sketchProps, getPropsObject(entity.properties));

    // If editing, update handles color if needed
    if (activeEditRef.current?.updateHandles) {
      const color = entity.polygon ? sketchProps.fill || '#AAAAAA' : sketchProps.stroke || '#000000';
      activeEditRef.current.updateHandles(color);
    }
  }, [sketchProps]);

  // Load Sketch Items into Cesium
  useEffect(() => {
    if (!selectedSketch?.items?.length) return;
    if (selectedSketch?.visible === false) {
      viewerRef.current?.dataSources.removeAll();
      return;
    }

    if (activeEditRef.current) return;
    viewerRef.current?.dataSources.removeAll();

    selectedSketch.items.forEach((item: { geojson: string; id: any; type: any }) => {
      const parsed = typeof item.geojson === 'string' ? JSON.parse(item.geojson) : item.geojson;
      Cesium.GeoJsonDataSource.load(parsed, { clampToGround: false })
        .then((dataSource) => {
          viewerRef.current.dataSources.add(dataSource);
          const entities = dataSource.entities.values;

          entities.forEach((entity) => {
            if (selectedSketchItem?.id === item.id) {
              editingEntityRef.current = entity;
            }
            (entity as any).__sketchItemId = item.id;
            (entity as any).__sketchItemType = item.type;

            const rawProps = getPropsObject(entity.properties) || {};
            const style = selectedSketchItem?.id === item.id ? sketchProps || {} : rawProps.style || rawProps || {};
            applyEntityStyles(entity, style, rawProps);
          });
        })
        .catch(console.error);
    });
  }, [selectedSketch, sketchProps]); // Note: sketchProps dep might need throttling or removal if causes re-loads

  // Selection and Editing Interaction
  useEffect(() => {
    if (!viewerRef.current) return;
    const viewer = viewerRef.current;
    const handler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);

    handler.setInputAction((movement: any) => {
      if (selectedSketch?.visible === false) return;

      const picked = viewer.scene.pick(movement.position);
      if (!Cesium.defined(picked)) return;

      const entity = picked.id as any;
      if (!entity || !entity.__sketchItemId) return;

      const sketchItemId = entity.__sketchItemId;
      const item = selectedSketch?.items?.find((i: SketchItem) => i.id === sketchItemId);
      if (!item) return;

      // Prepare for editing
      if (activeEditRef.current) {
        activeEditRef.current.destroy();
        activeEditRef.current = null;
      }

      const extracted = extractPositionsFromEntity(entity);
      if (!extracted.length) return;

      pointsRef.current = extracted;
      editingEntityRef.current = entity;

      const now = Cesium.JulianDate.now();
      const rawProps = entity.properties?.getValue(now) || {};
      const style = rawProps.style || {};
      const entityColor = entity.polygon ? style.fill || '#AAAAAA' : style.lineColour || style.stroke || '#000000';

      // Attach editable logic
      if (entity.polygon) {
        activeEditRef.current = makePolygonEditable(viewer, entity, pointsRef.current, entityColor, () => {
          geometryEditedRef.current = true;
        });
      }
      if (entity.polyline) {
        activeEditRef.current = makePolylineEditable(viewer, entity, pointsRef.current, entityColor, () => {
          geometryEditedRef.current = true;
        });
      }
      if (entity.position && entity.point) {
        activeEditRef.current = makePointEditable(viewer, entity, pointsRef.current, () => {
          geometryEditedRef.current = true;
        });
      }

      setSelectedSketchItem(item);
      setDrawingMode(item.type);
      setSketchDrawerState('configure');
      setSketchDrawerVisible(true);

      try {
        const props = typeof item.properties === 'string' ? JSON.parse(item.properties) : item.properties;
        setSketchPropsState(props || {});
      } catch (e) {
        console.error(e);
      }
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

    return () => handler.destroy();
  }, [selectedSketch]);

  // Drawing Logic (New Entities)
  useEffect(() => {
    if (selectedSketchItem) return;
    if (!drawingMode) return;

    const viewer = viewerRef.current;
    const handler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);

    const canDraw = sketchDrawerVisible && sketchDrawerState === 'view' && !selectedSketchItem;

    if (!canDraw || !drawingMode) {
      return;
    }

    const isReadyToDraw = sketchDrawerVisible && sketchDrawerState === 'view' && drawingMode !== '' && !selectedSketchItem;

    if (isReadyToDraw) {
      viewer.canvas.style.cursor = 'crosshair';
    } else {
      viewer.canvas.style.cursor = 'default';
    }

    if (drawingMode === 'line') {
      handler.setInputAction((click: any) => {
        if (selectedSketchItemRef.current) return;
        viewer.entities.removeAll();
        const pos = viewer.scene.pickPosition(click.position);
        if (Cesium.defined(pos)) {
          pointsRef.current.push(pos);
          const lineColor = sketchProps.stroke || '#000000';
          const polyline = viewer.entities.add({
            polyline: {
              positions: new Cesium.CallbackProperty(() => pointsRef.current, false),
              width: sketchProps.strokeWidth || 2,
              material: Cesium.Color.fromCssColorString(lineColor).withAlpha((sketchProps.lineOpacity ?? 100) / 100),
            },
          });
          makePolylineEditable(viewer, polyline, pointsRef.current, lineColor, () => {
            geometryEditedRef.current = true;
          });
        }
      }, Cesium.ScreenSpaceEventType.LEFT_CLICK);
    } else if (drawingMode === 'polygon') {
      handler.setInputAction((click: any) => {
        if (selectedSketchItemRef.current) return;
        viewer.entities.removeAll();
        const pos = viewer.scene.pickPosition(click.position);
        pointsRef.current.push(pos);
        const fillColor = sketchProps.fill || '#AAAAAA';
        const polygon = viewer.entities.add({
          polygon: {
            hierarchy: new Cesium.CallbackProperty(() => new Cesium.PolygonHierarchy(pointsRef.current), false),
            material: Cesium.Color.fromCssColorString(fillColor).withAlpha((sketchProps.fillOpacity ?? 100) / 100),
            outline: sketchProps.polygonOutline ?? true,
            outlineColor: Cesium.Color.fromCssColorString(sketchProps.outline || '#000000').withAlpha((sketchProps.outlineOpacity ?? 100) / 100),
            outlineWidth: sketchProps.outlineWeight || 2,
          },
        });
        makePolygonEditable(viewer, polygon, pointsRef.current, fillColor, () => {
          geometryEditedRef.current = true;
        });
      }, Cesium.ScreenSpaceEventType.LEFT_CLICK);
    } else if (drawingMode === 'point' || drawingMode === 'text') {
      handler.setInputAction((click: any) => {
        if (selectedSketchItemRef.current) return;
        const pos = viewer.scene.pickPosition(click.position);
        if (!Cesium.defined(pos)) return;

        viewer.entities.removeAll();

        if (drawingMode === 'text') {
          setPendingTextClick(pos);
          return;
        }

        const entityOps: Cesium.Entity.ConstructorOptions = {
          position: pos,
          point: {
            pixelSize: 10,
            color: Cesium.Color.WHITE,
            outlineColor: Cesium.Color.BLACK,
            outlineWidth: 2,
          },
        };
        viewer.entities.add(entityOps);
        pointsRef.current = [pos];
        setSelectedSketch({ ...selectedSketch, position: pos });
      }, Cesium.ScreenSpaceEventType.LEFT_CLICK);
    }

    return () => {
      handler.destroy();
      if (viewer.canvas) {
        viewer.canvas.style.cursor = 'default';
      }
      if (!selectedSketchItem) {
        // cleanup temp drawing if unmounted mid-draw
        // startPointRef.current = null;
      }
    };
  }, [drawingMode, selectedSketchItem, sketchProps, sketchDrawerVisible, sketchDrawerState]);

  const confirmTextLabel = (label: string) => {
    const viewer = viewerRef.current;
    if (!pendingTextClick || !viewer) return;
    const pos = pendingTextClick;
    viewer.entities.add({
      position: pos,
      point: {
        pixelSize: 10,
        color: Cesium.Color.WHITE,
        outlineColor: Cesium.Color.BLACK,
        outlineWidth: 2,
      },
      label: {
        text: label,
        font: '12pt monospace',
        style: Cesium.LabelStyle.FILL_AND_OUTLINE,
        outlineWidth: 2,
        verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
        pixelOffset: new Cesium.Cartesian2(0, -9),
      },
    });
    pointsRef.current = [pos];
    setSelectedSketch({ ...selectedSketch, label, position: pos });
    setPendingTextClick(null);
  };

  const cancelTextLabel = () => {
    setPendingTextClick(null);
  };

  // API Calls & Data Formatting
  const createGeoJsonFromPoints = (styleOptions?: any) => {
    if (!pointsRef.current || pointsRef.current.length === 0) return null;

    const toCoord = (p: Cesium.Cartesian3) => {
      const c = Cesium.Cartographic.fromCartesian(p);
      return [Cesium.Math.toDegrees(c.longitude), Cesium.Math.toDegrees(c.latitude), c.height];
    };

    const properties = {
      style: styleOptions || selectedSketch?.options || sketchProps || {},
      label: selectedSketch?.label ?? styleOptions?.label,
      _type: drawingMode,
    };

    if (drawingMode === 'point' || drawingMode === 'text') {
      return {
        type: 'Feature',
        geometry: { type: 'Point', coordinates: toCoord(pointsRef.current[0]) },
        properties: { ...properties, _type: drawingMode },
      };
    }
    if (drawingMode === 'line') {
      const coords = pointsRef.current.map(toCoord);
      if (coords.length < 2) return null;
      return {
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: coords },
        properties: { ...properties, _type: 'line' },
      };
    }
    if (drawingMode === 'polygon') {
      const coords = pointsRef.current.map(toCoord);
      if (coords.length < 3) return null;
      // Ensure close
      const first = coords[0];
      const last = coords[coords.length - 1];
      if (first[0] !== last[0] || first[1] !== last[1]) coords.push(first);
      return {
        type: 'Feature',
        geometry: { type: 'Polygon', coordinates: [coords] },
        properties: { ...properties, _type: 'polygon' },
      };
    }
    return null;
  };

  const handleSaveSketchItem = async () => {
    if (!selectedSketch || !selectedSketchItem) return;

    let geoObj: any = null;
    const isText = selectedSketchItem.type === 'text';
    const hasPoints = pointsRef.current && pointsRef.current.length > 0;

    if (hasPoints && (geometryEditedRef.current || isText)) {
      geoObj = createGeoJsonFromPoints(sketchProps);
    } else {
      geoObj = typeof selectedSketchItem.geojson === 'string' ? JSON.parse(selectedSketchItem.geojson) : selectedSketchItem.geojson;
      if (geoObj?.properties) geoObj.properties.style = sketchProps;
    }

    if (!geoObj) return;

    const geojsonStr = JSON.stringify(geoObj);
    const propertiesStr = JSON.stringify(sketchProps);

    const res = await editSketchItemAsync({
      id: selectedSketchItem.id!,
      payload: {
        geojson: geojsonStr,
        type: selectedSketchItem.type,
        properties: propertiesStr,
      },
    });

    if (res) {
      const updatedItems = (selectedSketch.items || []).map((item: any) =>
        item.id === selectedSketchItem.id ? { ...item, geojson: geojsonStr, properties: propertiesStr } : item,
      );
      const updatedSketch = { ...selectedSketch, items: updatedItems, config: { ...sketchProps } };
      setSketches((prev) => prev.map((s) => (s.id === selectedSketch.id ? updatedSketch : s)));
    }

    setSketchDrawerVisible(false);
    geometryEditedRef.current = false;
    if (drawingMode !== 'text') pointsRef.current = [];
  };

  const createNewSketchItem = async () => {
    if (!selectedSketch) return;
    const geoObj = createGeoJsonFromPoints(sketchProps);
    if (!geoObj) return;

    const geojson = JSON.stringify(geoObj, null, 2);
    const payload = {
      geojson,
      type: drawingMode,
      properties: JSON.stringify({ ...(selectedSketch?.options || {}), ...(sketchProps || {}) }),
    };

    const res = await addSketchItemsAsync({
      sketchId: selectedSketch.id!,
      items: [payload],
    });
    if (res) {
      const newItem = res[0];
      viewerRef.current?.entities.removeAll();
      pointsRef.current = [];
      startPointRef.current = null;
      const updatedSketch = { ...selectedSketch, items: [...(selectedSketch.items || []), newItem] };
      setSketches((prev) => prev.map((s) => (s.id === selectedSketch.id ? updatedSketch : s)));
      setSelectedSketch(updatedSketch);
      setDrawingMode(newItem.type || drawingMode);
      setSelectedSketchItem(newItem);
      setSketchDrawerState('configure');
    }
  };

  const deleteItem = async () => {
    if (!selectedSketchItem || !selectedSketch) return;
    await deleteSketchItemAsync({ id: selectedSketchItem.id! });
    const updatedItems = selectedSketch.items.filter((item: any) => item.id !== selectedSketchItem.id);
    const updatedSketch = { ...selectedSketch, items: updatedItems };
    setSketches((prev) => prev.map((s) => (s.id === selectedSketch.id ? updatedSketch : s)));
    setSelectedSketch(updatedSketch);
    setSelectedSketchItem(null);
    setDrawingMode('');
    pointsRef.current = [];
    setSketchDrawerVisible(false);
  };

  const deleteSketch = async (sketchId: string) => {
    const res = await deleteMapSketchAsync({ sketchId });
    if (res) {
      setSketches((prev) => prev.filter((s) => s.id !== sketchId));
      if (selectedSketch?.id === sketchId) {
        viewerRef.current?.dataSources.removeAll();
        viewerRef.current?.entities.removeAll();
        setSelectedSketch(null);
        setSketchDrawerVisible(false);
      } else {
        viewerRef.current?.dataSources.removeAll();
      }
    }
  };

  return {
    sketches,
    setSketches,
    drawingMode,
    setDrawingMode,
    pointsRef,
    viewerRef,
    handleSaveSketchItem,
    createNewSketchItem,
    deleteItem,
    pendingTextClick,
    confirmTextLabel,
    cancelTextLabel,
    createMapSketch: useCallback((sketchData: Sketch) => createMapSketchAsync({ sketchData }), [createMapSketchAsync]),
    editMapSketch: useCallback((sketchData: Sketch) => editMapSketchAsync({ sketchData }), [editMapSketchAsync]),
    deleteSketch,
  };
};
